import React from "react";
import { Link, useLocation } from "react-router-dom";
import { BrandMark } from "./brand";
import { Reveal, usePointerTilt, useReducedMotion } from "./motion";
import { PostcardSurface } from "./postcard_surface";
import { PhotoPlaceholder } from "./postcard";
import { getGuestFacilities, getHealth, getCustomerToken, customerMe } from "./api";
import { IndependentServiceDisclosure, TrustConfidence } from "./day25";
import { I } from "./icons";
import { ShareWithFriend } from "./share";
import { SiteLanguageSwitcher, LanguageSelect } from "./chrome";
import { useSiteLocale } from "./flow_copy";
import { PriceStamp } from "./stamp";
import { SEASON, seasonState, formatSendBy } from "./seasonal";
import "./seasonal.css";

/* The marketing homepage. MASTER_SPEC Draft80 §16C: LUX-01 architecture,
 * LUX-02 section order, LUX-18 conversion, LUX-19 trust.
 *
 * The section order is the contract's, and each one answers a question the
 * customer is actually asking: what is this, is it physical, will the facility
 * take it, what does it cost, what happens after I pay. (V35, owner
 * 2026-10-29: the homepage runs five sections — the LUX-02 order slimmed on
 * her direction; see the LuxuryHome composition note.)
 *
 * Two rules run through the whole file. Nothing here invents social proof —
 * no testimonials, no counters, no "23 people ordered today", no urgency, no
 * scarcity, no certification badges — because this audience is being sold to
 * badly by everyone else already. And every capability claim is gated on the
 * capability actually being on: "Secure checkout" renders as a live claim only
 * when the production payment path is verified, and says something else
 * truthful when it is not.
 */

const PRICE = "$3.99";

/* PostcardSurface UI-chrome labels from the active site locale. */
function cardLabels(t) {
  return {
    front: t.homeProofFront,
    back: t.homeProofBack,
    toggleLabel: t.cardToggleAria,
    note: t.cardNote,
    photoAria: t.cardPhotoAria,
    photoAlt: t.cardPhotoAlt,
    photoEmpty: t.cardPhotoEmpty,
    photoEmptySub: t.cardPhotoEmptySub,
    messageEmpty: t.cardMessageEmpty,
    zoneAddress: t.cardZoneAddress,
    zoneAddressSub: t.cardZoneAddressSub,
    zoneBarcode: t.cardZoneBarcode,
  };
}

function useTruthGates() {
  // The trust line makes claims. They are read from the server rather than
  // hardcoded, so a build that cannot take payment cannot say it can.
  const [gates, setGates] = React.useState({ loading: true, payment_enabled: false });
  React.useEffect(() => {
    let live = true;
    getHealth()
      .then((h) => live && setGates({
        loading: false,
        payment_enabled: Boolean(h && h.payment_enabled),
        mail_enabled: Boolean(h && h.mail_submission_enabled),
      }))
      .catch(() => live && setGates({ loading: false, payment_enabled: false }));
    return () => { live = false; };
  }, []);
  return gates;
}

export function useFacilities() {
  const [rows, setRows] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    getGuestFacilities()
      .then((r) => live && setRows(Array.isArray(r) ? r : []))
      .catch(() => live && setRows([]));
    return () => { live = false; };
  }, []);
  return rows;
}

/* ── 01 Hero ─────────────────────────────────────────────────────────────── */

function Hero() {
  const stageRef = usePointerTilt({ max: 4 });
  const { t } = useSiteLocale();
  return (
    <section className="cc-hero" aria-labelledby="hero-title">
      {/* V35 slimming (owner, 2026-10-29): the seasonal strip rides at the
          top of the hero as one compact line instead of its own band
          between sections. Its function is unchanged — it renders only
          while the Christmas send-by date is ahead and links to /christmas. */}
      <SeasonalBanner />
      <div className="cc-hero-light" aria-hidden="true" />
      <div className="cc-shell cc-hero-grid">
        <div className="cc-hero-copy">
          <p className="cc-eyebrow">{t.homeHeroEyebrow}</p>
          <div className="cc-hero-badges">
            <span className="cc-open-badge" lang="en">NOW OPEN</span>
            <span className="cc-postcard-pill">{t.homeHeroPostcard}</span>
          </div>
          <h1 id="hero-title" className="cc-display cc-hero-title">
            <span className="cc-duo-1">{t.homeHeroTitle1}</span>{" "}
            <span className="cc-duo-2">{t.homeHeroTitle2}</span>
          </h1>
          <p className="cc-lede cc-hero-lede">
            {t.homeHeroLede}
          </p>
          <div className="cc-hero-actions">
            <Link className="cc-btn cc-btn-primary cc-hero-cta" to="/send">
              {t.homeHeroCta}
            </Link>
          </div>
          {/* V37 giveaway entry: one compact line beside the hero CTA so
              visitors arriving from the Facebook post find the free-card
              page. Inside the hero on purpose — the V35 homepage keeps its
              five sections. */}
          <div className="cc-giveaway-strip">
            <span>{t.homeGiveawayText}</span>
            <Link className="cc-giveaway-link" to="/giveaway">{t.homeGiveawayLink}</Link>
          </div>
          {/* V35 (owner, 2026-10-29): the PriceStamp — her picked price
              treatment — joins the hero beside the steps/fees lines. The
              component itself is unchanged; only this placement is new.
              It was previously rendered on /pricing and /christmas only. */}
          <div className="cc-hero-price">
            <div className="cc-hero-price-text">
              <p className="cc-hero-steps">{t.homeHeroSteps}</p>
              <p className="cc-hero-fees">{t.homeNoHiddenFees}</p>
              <div className="cc-hero-links">
                <Link className="cc-hero-link" to="/send">{t.homeHeroSecondary}</Link>
              </div>
            </div>
            <PriceStamp t={t} price={PRICE} />
          </div>
        </div>

        <div className="cc-hero-stage" ref={stageRef}>
          <PostcardSurface
            id="hero-card"
            size="lg"
            labels={cardLabels(t)}
            photoNode={<PhotoPlaceholder priority />}
            message={t.homeHeroSampleMessage || "Thinking about you every single day.\n\nThe garden finally came in. Dad says hello.\n\nWe are counting the weeks.\n\nLove,\nMom"}
            proofNote={false}
          />
        </div>
      </div>
    </section>
  );
}

function TrustLine({ gates }) {
  const { t } = useSiteLocale();
  /* LUX-02: "Secure checkout" is truth-gated production copy. Until the
     production payment path is verified it may not be shown as a live
     capability, so the slot says what is actually true instead. */
  const checkout = gates.payment_enabled
    ? t.homeTrustSecure
    : t.homeTrustNotYet;
  const items = [t.homeTrustNoAccount, checkout, t.homeTrustRules];
  return (
    <ul className="cc-trustline" aria-label={t.homeTrustLabel}>
      {items.map((label, i) => (
        <li key={i} className="cc-trust-tile">
          <span className="cc-trust-chip" aria-hidden="true">
            {["◈", "⬢", "✦"][i % 3]}
          </span>
          <span>{label}</span>
        </li>
      ))}
    </ul>
  );
}

/* ── 02 A real postcard ──────────────────────────────────────────────────── */

export function PhysicalProof({ embedded = false }) {
  const [side, setSide] = React.useState("front");
  const reduced = useReducedMotion();
  const { t } = useSiteLocale();
  return (
    <Reveal as="section" className={`cc-section cc-proof${embedded ? " cc-embed" : ""}`} aria-labelledby="proof-title">
      <div className="cc-shell cc-split">
        <div className="cc-split-copy">
          <p className="cc-eyebrow">{t.homeProofEyebrow}</p>
          <h2 id="proof-title" className="cc-h2">{t.homeProofTitle}</h2>
          <p className="cc-lede">
            {t.homeProofLede}
          </p>
          <dl className="cc-proof-list">
            <div>
              <dt>{t.homeProofFront}</dt>
              <dd>{t.homeProofFrontBody}</dd>
            </div>
            <div>
              <dt>{t.homeProofBack}</dt>
              <dd>{t.homeProofBackBody}</dd>
            </div>
            <div>
              <dt>{t.homeProofSide}</dt>
              <dd>{t.homeProofSideBody}</dd>
            </div>
          </dl>
          <p className="cc-small">
            {reduced ? t.homeProofFlipHintReduced : t.homeProofFlipHint}
          </p>
        </div>
        <div className="cc-split-stage">
          <PostcardSurface
            id="proof-card"
            side={side}
            onSideChange={setSide}
            labels={cardLabels(t)}
            photoNode={<PhotoPlaceholder />}
            message={t.homeProofSampleMessage || "Grandma read your letter out loud twice.\n\nWe put your drawing on the fridge.\n\nSee you on visitation Sunday."}
          />
        </div>
      </div>
    </Reveal>
  );
}

/* ── Seasonal banner (harvest integration, 2026-10-21) ─────────────────
 *
 * The one campaign door on the homepage. Date-driven from seasonal.jsx's
 * SEASON. V34 (2026-10-25): the homepage strip now shows ONLY while the
 * send-by date is still ahead (state "open") and hides itself the day
 * after the send-by date (Dec 12, 2026 for Christmas 2026), so the
 * homepage never carries a late-season line. The /christmas page keeps
 * its own late copy. The component checks the date on every render.
 * On the send-by day itself the countdown says "last day" instead of
 * "0 days left".
 */

export function SeasonalBanner({ now } = {}) {
  const { t, locale } = useSiteLocale();
  const current = now || new Date();
  const state = seasonState(current);
  if (state !== "open") return null;
  const today = new Date(current.getTime());
  today.setHours(0, 0, 0, 0);
  const deadlineDay = new Date(SEASON.sendBy.year, SEASON.sendBy.month - 1, SEASON.sendBy.day);
  const daysLeft = Math.max(0, Math.round((deadlineDay - today) / 86400000));
  return (
    <div className="cc-season-banner">
      <p>{t.seasonalBannerOpen(formatSendBy(locale))} <strong>{t.seasonalBannerDaysLeft(daysLeft)}</strong></p>
      <Link to="/christmas">{t.seasonalBannerLink}</Link>
    </div>
  );
}

/* ── 05 Compliance reassurance ──────────────────────────────────────────── */

export function Compliance({ facilities, embedded = false, showFacilities = true, surface = "home-trust" }) {
  const rows = facilities || [];
  const blocked = rows.filter((f) => !f.orderable);
  const { t } = useSiteLocale();
  return (
    <Reveal as="section" className={`cc-section cc-compliance${embedded ? " cc-embed" : ""}`} aria-labelledby="rules-title">
      <div className={`cc-shell${showFacilities ? " cc-split cc-split-narrow" : ""}`}>
        <div className="cc-split-copy">
          <p className="cc-eyebrow">{t.homeComplianceEyebrow}</p>
          <h2 id="rules-title" className="cc-h2">{t.homeComplianceTitle}</h2>
          <p className="cc-lede">
            {t.homeComplianceLede}
          </p>
          <ul className="cc-checklist">
            {t.homeComplianceChecks.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
        {showFacilities && <div className="cc-facilities">
          <h3 className="cc-h3 cc-facilities-title">{t.homeFacilitiesTitle}</h3>
          {facilities === null ? (
            <p className="cc-small" role="status">{t.homeFacilitiesLoading}</p>
          ) : (
            <ul className="cc-facility-list">
              {rows.map((f) => (
                <li key={f.slug} className="cc-facility">
                  <span className="cc-facility-name">{f.name}</span>
                  {f.address && (
                    <span className="cc-facility-addr">
                      <span>{t.homeComplianceAddressLabel}</span>
                      <span className="cc-mono">{f.address}, {f.city}, {f.state} {f.zip}</span>
                    </span>
                  )}
                  {f.orderable ? (
                    <span className="cc-status cc-status-ok">
                      <span aria-hidden="true">●</span> {t.homeFacilityAvailable}
                    </span>
                  ) : (
                    <span className="cc-status cc-status-warn">
                      <span aria-hidden="true">▲</span> {t.homeFacilityUnavailable}
                    </span>
                  )}
                  {!f.orderable && f.suspended_reason && (
                    <p className="cc-facility-reason">{f.suspended_reason}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
          {blocked.length > 0 && (
            <p className="cc-small cc-facility-note">
              {t.homeFacilityNote}
            </p>
          )}
        </div>}
      </div>
      {/* V35 slimming (owner, 2026-10-29): the standalone TrustSection is
          folded into this section in shortened form. TrustConfidence is
          rendered unchanged, with the same surface and experiment props,
          because it is analytics-instrumented: it fires
          trust_component_viewed for this surface and runs the day25 trust
          experiment assignment. The five trust points keep their copy but
          lose the separate section shell, heading and per-item reveals;
          the independent-service disclosure keeps its place alongside. */}
      <div className="cc-shell">
        <TrustConfidence surface={surface} experiment embedded />
        {/* Shortened form: the five trust points as one checklist — the
            points themselves, without the five elaborating paragraphs the
            standalone section carried. */}
        <ul className="cc-checklist">
          {[t.homeReasons[1], ...t.homeTrustItems].map((item) => (
            <li key={item.t}>{item.t}</li>
          ))}
        </ul>
        <p className="cc-trust-close">{t.homeTrustClose}</p>
        <IndependentServiceDisclosure />
      </div>
    </Reveal>
  );
}

/* ── 06 Configurator preview — REMOVED 2026-10-23 (owner review: the
 * homepage showed the postcard three times; the teaser repeated the hero
 * and proof cards. The live configurator is the /send flow itself.) ──── */

/* ── 10 FAQ ─────────────────────────────────────────────────────────────── */

/* V42 (owner, 2026-10-31: "I don't like how far down you can scroll on the
 * front page… break up into individual pages"): the home FAQ moved to the
 * top of /faq as its "Before you start" block. Same translated questions. */
export function HomeFaqBlock() {
  const { t } = useSiteLocale();
  return (
    <section className="cc-faq cc-faq-moved" aria-labelledby="faq-start-title">
      <p className="cc-eyebrow">{t.homeFaqEyebrow}</p>
      <h2 id="faq-start-title" className="cc-h2">{t.homeFaqTitle}</h2>
      <div className="cc-faq-list">
        {t.homeFaqItems.map((item) => (
          <details key={item.q} className="cc-faq-item">
            <summary>{item.q}</summary>
            <p className="cc-body">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ── V42 compact story: three steps, one line each, then the door to the
 * full walkthrough. ───────────────────────────────────────────────────── */

function Process() {
  const { t } = useSiteLocale();
  return (
    <section className="cc-section cc-how" aria-labelledby="home-how-title">
      <div className="cc-shell">
        <h2 id="home-how-title" className="cc-h2">{t.homeHowEyebrow}</h2>
        <ol className="cc-how-steps">
          {t.homeHow.map((s, i) => (
            <li key={s.t} className="cc-how-step">
              <span className="cc-how-num" aria-hidden="true">{i + 1}</span>
              <div>
                <h3 className="cc-h3">{s.t}</h3>
                <p className="cc-body">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
        <Link className="cc-home-more-link" to="/how-it-works">{t.homeHowMore} <span aria-hidden="true">→</span></Link>
      </div>
    </section>
  );
}

/* ── V42 closing band: where the longer content went, the trust line, the
 * founder line and one last button. ──────────────────────────────────── */

const MORE_LINKS = [
  ["/facilities", "homeMoreFacilities"],
  ["/facility-rules", "homeNavFacilityRules"],
  ["/faq", "homeMoreFaq"],
  ["/pricing", "homeHeaderPricing"],
  ["/about", "homeNavAbout"],
  ["/contact", "homeNavContact"],
];

function Closing({ gates }) {
  const { t } = useSiteLocale();
  return (
    <section className="cc-section cc-home-close" aria-labelledby="home-more-title">
      <div className="cc-shell">
        <h2 id="home-more-title" className="cc-h3 cc-home-more-title">{t.homeMoreLabel}</h2>
        <nav aria-labelledby="home-more-title">
          <ul className="cc-home-more">
            {MORE_LINKS.map(([href, key]) => (
              <li key={href}><Link to={href}>{t[key]}</Link></li>
            ))}
          </ul>
        </nav>
        <TrustLine gates={gates} />
        <p className="cc-why-founder">{t.whyFounder}</p>
        <div className="cc-home-final">
          <Link className="cc-btn cc-btn-primary cc-hero-cta" to="/send">
            {t.homeFinalCta} — {PRICE}
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

export function LuxuryHome() {
  const gates = useTruthGates();
  /* V42 (owner, 2026-10-31): the front page is three bands — hero (price
     stamp and the main button), a compact how-it-works, and a closing band
     with the trust line and links to the pages that now hold the longer
     content: the card proof (/how-it-works), facilities + mail rules +
     trust points (/facilities), and the FAQ (/faq). */
  return (
    <div className="cc cc-home">
      <Hero />
      <Process />
      <Closing gates={gates} />
    </div>
  );
}

/* ── Header and footer (LUX-01) ─────────────────────────────────────────── */

/* V44 (2026-11-02, owner): customer sign-in in the header. Inside the /send
 * flow the pill opens in a new tab: the unsaved card draft is deliberately
 * never persisted, so navigating away would lose it. The header only
 * reads the token the account pages already store (cc-customer-token) and
 * confirms it once per token with /api/auth/me — it never clears the token
 * itself (RequireCustomer on /account does that). Re-read on every route
 * change so signing in or out updates the header without a reload. */
const _ccSignedInCache = { token: "", ok: false };
function useCustomerSignedIn() {
  const { pathname } = useLocation();
  const [signedIn, setSignedIn] = React.useState(() => {
    const tok = getCustomerToken();
    return !!tok && (_ccSignedInCache.token !== tok || _ccSignedInCache.ok);
  });
  React.useEffect(() => {
    let alive = true;
    const tok = getCustomerToken();
    if (!tok) { setSignedIn(false); return undefined; }
    if (_ccSignedInCache.token === tok) { setSignedIn(_ccSignedInCache.ok); return undefined; }
    setSignedIn(true);
    Promise.resolve()
      .then(() => customerMe())
      .then((u) => { _ccSignedInCache.token = tok; _ccSignedInCache.ok = !!u; if (alive) setSignedIn(!!u); })
      .catch(() => { _ccSignedInCache.token = tok; _ccSignedInCache.ok = false; if (alive) setSignedIn(false); });
    return () => { alive = false; };
  }, [pathname]);
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "cc-customer-token") setSignedIn(!!e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  return signedIn;
}

export function SiteHeader({ isHome = false, inFlow = false }) {
  const [open, setOpen] = React.useState(false);
  const closeRef = React.useRef(null);
  const { t } = useSiteLocale();
  const signedIn = useCustomerSignedIn();

  // Escape closes the sheet, and focus returns to the control that opened it.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className={`cc cc-header${isHome ? " cc-header-home" : ""}${inFlow ? " cc-header-flow" : ""}`}>
      <div className="cc-shell cc-header-inner">
        <Link to="/" className="cc-header-brand" aria-label="CareCard Express, home">
          <BrandMark size="sm" />
        </Link>

        <nav className="cc-header-nav" aria-label="Primary">
          <Link to="/how-it-works">{t.homeHeaderHow}</Link>
          <Link to="/pricing">{t.homeHeaderPricing}</Link>
          <Link to="/lookup">{t.homeHeaderTrack}</Link>
          <Link to="/faq">{t.homeHeaderHelp}</Link>
        </nav>

        <div className="cc-header-actions">
          {/* BUILD-20: visible, guest-accessible language switcher, persistent
              in the global header. Native control, labeled in the active
              language; runtime switching, local-first, draft-safe. */}
          <LanguageSelect id="cc-header-language" />
          {signedIn ? (
            <Link className="cc-btn cc-btn-secondary cc-header-signin" to="/account"
              {...(inFlow ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
              <span className="cc-signin-full">{t.homeHeaderMyAccount}</span>
              <span className="cc-signin-short">{t.homeHeaderMyAccountShort}</span>
            </Link>
          ) : (
            <Link className="cc-btn cc-btn-secondary cc-header-signin" to="/login"
              {...(inFlow ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
              <span className="cc-signin-full">{t.homeHeaderSignIn}</span>
              <span className="cc-signin-short">{t.homeHeaderSignInShort}</span>
            </Link>
          )}
          <Link className="cc-btn cc-btn-primary cc-header-cta" to="/send">
            {t.homeHeroCta}<span className="cc-header-cta-price"> — {PRICE}</span>
          </Link>
          <button
            type="button"
            className="cc-header-menu"
            aria-expanded={open}
            aria-controls="cc-nav-sheet"
            ref={closeRef}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="cc-sr-only">{open ? t.homeMenuClose : t.homeMenuOpen}</span>
            <span aria-hidden="true" className="cc-header-menu-bars" data-open={open} />
          </button>
        </div>
      </div>

      <div id="cc-nav-sheet" className="cc-nav-sheet" hidden={!open}>
        <nav aria-label="Secondary">
          <Link to="/how-it-works" onClick={() => setOpen(false)}>{t.homeHeaderHow}</Link>
          <Link to="/pricing" onClick={() => setOpen(false)}>{t.homeHeaderPricing}</Link>
          <Link to="/lookup" onClick={() => setOpen(false)}>{t.homeNavTrackOrder}</Link>
          <Link to="/faq" onClick={() => setOpen(false)}>{t.homeHeaderHelp}</Link>
          <Link to="/facility-rules" onClick={() => setOpen(false)}>{t.homeNavFacilityRules}</Link>
          <Link to="/about" onClick={() => setOpen(false)}>{t.homeNavAbout}</Link>
          <Link to="/contact" onClick={() => setOpen(false)}>{t.homeNavContact}</Link>
          {signedIn ? (
            <Link className="cc-nav-sheet-account" to="/account" onClick={() => setOpen(false)}>{t.homeHeaderMyAccount}</Link>
          ) : (
            <>
              <Link className="cc-nav-sheet-account" to="/login" onClick={() => setOpen(false)}>{t.homeHeaderSignIn}</Link>
              <Link to="/signup" onClick={() => setOpen(false)}>{t.homeHeaderCreateAccount}</Link>
            </>
          )}
        </nav>
      </div>


    </header>
  );
}

const FOOTER_LINKS = [
  ["/send", "homeFooterSend"],
  ["/lookup", "homeFooterTrack"],
  ["/faq", "homeFooterHelp"],
  ["/facility-rules", "homeFooterFacilityRules"],
  // WS15 (2026-10-13): /facilities, /services and /mail-guide were in the
  // sitemap but no working link anywhere reached them (their only inbound
  // links were the info-page chrome's then-untranslated hash links). They
  // are first-class footer destinations now.
  ["/facilities", "homeFooterFacilities"],
  ["/services", "navServices"],
  ["/mail-guide", "navMailGuide"],
  ["/content-policy", "homeFooterContentPolicy"],
  ["/refund-policy", "homeFooterRefunds"],
  ["/cancellation-policy", "homeFooterCancelling"],
  ["/data-retention", "homeFooterDataRetention"],
  ["/privacy", "homeFooterPrivacy"],
  ["/terms", "homeFooterTerms"],
  ["/acceptable-use", "homeFooterAcceptableUse"],
  ["/accessibility", "homeFooterAccessibility"],
  ["/security", "homeFooterSecurity"],
  ["/disclosures", "homeFooterDisclosures"],
  ["/status", "homeFooterStatus"],
];

export function SiteFooter() {
  const { t } = useSiteLocale();
  return (
    <footer className="cc cc-footer">
      <div className="cc-shell cc-footer-inner">
        <BrandMark size="sm" tagline taglineText={t.footerTagline} />
        <nav className="cc-footer-nav" aria-label="Footer">
          {FOOTER_LINKS.map(([href, key]) => (
            <Link key={href} to={href}>{t[key]}</Link>
          ))}
        </nav>
        {/* WS19: no inline margins here — the footer inner grid's single
            gap token owns every section gap (the old 4px/16px inline
            margins stacked on it and opened the dead voids). */}
        <div>
          <ShareWithFriend variant="footer" />
        </div>
        {/* WS15 + BUILD-23 (§20.23): the site's social doors. Instagram
            (@carecard.express, confirmed live 2026-10-13) and Facebook
            (owner's public profile, where the CareCard reels run —
            linked per the owner's 2026-10-19 advertising directive). */}
        <div className="cc-footer-socials">
          <a className="cc-footer-social" href="https://www.instagram.com/carecard.express" target="_blank" rel="noopener noreferrer">Instagram · @carecard.express</a>
          <a className="cc-footer-social" href="https://www.facebook.com/biancarodriguez679256/" target="_blank" rel="noopener noreferrer">Facebook · CareCard Express</a>
        </div>
        {/* BUILD-20: the language switcher lives in the footer too, so no
            page traps a visitor in a language they cannot change. */}
        <div>
          <LanguageSelect id="cc-footer-language" />
        </div>
        <p className="cc-small cc-footer-legal" lang="en">
          Independent service. Not affiliated with or endorsed by BSO.
        </p>
        <a className="cc-footer-contact" href="mailto:hello@carecardexpress.com">hello@carecardexpress.com</a>
      </div>
    </footer>
  );
}

export default LuxuryHome;
